import { useCallback } from "react";

import { toastLocalizedOperationCatcher } from "./alerts";
import { stopPresenting, usePresentation } from "./presentation";
import { useOverlayControls } from "./routing/hooks/use-overlay-controls";
import { useQueryParamsManager } from "./routing/hooks/use-query-params-manager";
import { PartialUpdateable } from "./type-utils";

import { FOLLOWING_QUERY_PARAM, SecondaryOverlay } from "components/constants";
import { ClassUserType, ReactionType, VoteType } from "components/server-types";
import { t } from "i18n/i18n";
import { storeApi, useAppSelector } from "store/index";
import { selectActiveClassSessionId } from "store/selectors";
import { selectCurrentClassUser } from "store/selectors/class-user";

const internalUpdateClassUser = async (classUserId: string, data: Partial<ClassUserType>) => {
  const classUser: PartialUpdateable<ClassUserType> = {
    id: classUserId,
    ...data,
  };
  await storeApi.class_users.partial_update(classUser).catch(
    toastLocalizedOperationCatcher("update_failure", {
      item: t("glossary.class_user"),
    }),
  );
};

export const useClassActions = () => {
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const currentClassSessionId = useAppSelector(selectActiveClassSessionId);
  const { isPresentingUser } = usePresentation();
  const { isSelected: isBreakoutSettingsSelected, togglePanel: toggleBreakoutSettingsPanel } =
    useOverlayControls(SecondaryOverlay.BreakoutSettings);
  const { isSelected: isChatSelected, togglePanel: toggleChatPanel } = useOverlayControls(
    SecondaryOverlay.ClassChat,
  );
  const { deleteQueryParams } = useQueryParamsManager();

  const updateClassUser = useCallback(
    async (data: Partial<ClassUserType>, classUserId?: string) => {
      try {
        const currentClassUserId = classUserId || currentClassUser?.id;

        if (!currentClassUserId) {
          console.warn("No class user found, cannot update", data);
          return;
        }

        await internalUpdateClassUser(currentClassUserId, data);
      } catch (error) {
        console.error("Error updating class user:", error);
      }
    },
    [currentClassUser?.id],
  );

  const joinClass = useCallback(
    (classUserId?: string) => {
      updateClassUser({ is_in_class_session: true }, classUserId);
    },
    [updateClassUser],
  );

  const leaveClass = useCallback(async () => {
    if (isPresentingUser && currentClassSessionId) {
      await stopPresenting(currentClassSessionId);
    }
    if (isBreakoutSettingsSelected) {
      toggleBreakoutSettingsPanel();
    }
    if (isChatSelected) {
      toggleChatPanel();
    }
    await updateClassUser({ is_in_class_session: false });
    deleteQueryParams([FOLLOWING_QUERY_PARAM]);
  }, [
    isPresentingUser,
    isBreakoutSettingsSelected,
    isChatSelected,
    updateClassUser,
    deleteQueryParams,
    currentClassSessionId,
    toggleBreakoutSettingsPanel,
    toggleChatPanel,
  ]);

  const toggleReaction = useCallback(
    (reaction: ReactionType) => {
      updateClassUser({
        reaction: currentClassUser?.reaction === reaction ? null : reaction,
      });
    },
    [updateClassUser, currentClassUser?.reaction],
  );

  const toggleVote = useCallback(
    (vote: VoteType) => {
      updateClassUser({
        vote: currentClassUser?.vote === vote ? null : vote,
      });
    },
    [updateClassUser, currentClassUser?.vote],
  );

  return {
    updateClassUser,
    joinClass,
    leaveClass,
    toggleReaction,
    toggleVote,
  };
};
